import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTransition, a } from "@react-spring/web";
import { GalleryImage } from "./galleryData";
import { MapPin, Tag } from "lucide-react";

interface MasonryItem extends GalleryImage {
  height: number;
}

interface GridItem extends MasonryItem {
  x: number;
  y: number;
  width: number;
  scaledHeight: number;
  rotation?: string;
}

interface MasonryProps {
  data: GalleryImage[];
  onHover: (id: string) => void;
  onLeave: () => void;
  hoveredImage: string | null;
}

const Masonry: React.FC<MasonryProps> = ({ data, onHover, onLeave, hoveredImage }) => {
  const [columns, setColumns] = useState<number>(2);
  
  // Different rotation angles for images to look like pinned photos
  const rotations = [
    "rotate-2", "-rotate-3", "rotate-1", "-rotate-2", "rotate-3", 
    "-rotate-1", "rotate-2", "-rotate-2", "rotate-0", "-rotate-3"
  ];

  useEffect(() => {
    const updateColumns = () => {
      if (window.matchMedia("(min-width: 1500px)").matches) {
        setColumns(5);
      } else if (window.matchMedia("(min-width: 1000px)").matches) {
        setColumns(4);
      } else if (window.matchMedia("(min-width: 600px)").matches) {
        setColumns(3);
      } else {
        setColumns(2); // Breakpoint for mobile devices
      }
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>(0);

  useEffect(() => {
    const handleResize = () => {
      if (ref.current) {
        setWidth(ref.current.offsetWidth);
      }
    };

    handleResize(); // Set initial width
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Add random heights to gallery items
  const masonryData = useMemo(() => {
    return data.map(item => {
      // Generate random heights between 200 and 400 for visual variety
      // Use a deterministic approach based on the ID to keep consistent across renders
      const idSeed = item.id ? parseInt(item.id.toString().replace(/[^0-9]/g, '')) : 0;
      const randomFactor = idSeed % 3; // 0, 1, or 2
      const height = 250 + (randomFactor * 75); // 250, 325, or 400
      
      return {
        ...item,
        height
      } as MasonryItem;
    });
  }, [data]);

  const [heights, gridItems] = useMemo<[number[], GridItem[]]>(() => {
    const heights = new Array(columns).fill(0);
    const gridItems = masonryData.map((child, index) => {
      const column = heights.indexOf(Math.min(...heights));
      const x = (width / columns) * column;
      const y = (heights[column] += child.height) - child.height;
      return {
        ...child,
        x,
        y,
        width: width / columns,
        scaledHeight: child.height,
        rotation: rotations[index % rotations.length]
      };
    });
    return [heights, gridItems];
  }, [columns, masonryData, width, rotations]);

  const transitions = useTransition(gridItems, {
    keys: (item) => item.id || item.src,
    from: ({ x, y, width, scaledHeight }) => ({ 
      x, 
      y, 
      width, 
      height: scaledHeight, 
      opacity: 0,
      scale: 0.8,
    }),
    enter: ({ x, y, width, scaledHeight }) => ({ 
      x, 
      y, 
      width, 
      height: scaledHeight, 
      opacity: 1,
      scale: 1,
    }),
    update: ({ x, y, width, scaledHeight }) => ({ 
      x, 
      y, 
      width, 
      height: scaledHeight,
    }),
    leave: { height: 0, opacity: 0, scale: 0.8 },
    config: { mass: 5, tension: 500, friction: 100 },
    trail: 25,
  });

  return (
    <div 
      ref={ref} 
      className="masonry relative" 
      style={{ height: Math.max(...heights) + 20 }}
    >
      {transitions((style, item, _, index) => (
        <a.div
          style={style}
          className="absolute p-3 will-change-transform"
        >
          <div
            className={`evidence-item group relative overflow-hidden rounded-lg transform transition-all duration-300 hover:z-20 hover:scale-105 shadow-md ${item.rotation}`}
            style={{
              width: "100%",
              height: "100%",
              transformOrigin: "center",
              background: "#121212",
              boxShadow: "0 4px 8px rgba(0,0,0,0.3)"
            }}
            onMouseEnter={() => onHover(item.id || String(index))}
            onMouseLeave={onLeave}
          >
            <div className="absolute inset-0 bg-black/60 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            {/* Main image */}
            <img 
              src={item.src} 
              alt={item.alt}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            
            {/* Image border to look like a photo */}
            <div className="absolute inset-0 border-4 border-white/10 pointer-events-none z-[11]"></div>
            
            {/* Image info overlay on hover */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3 z-[13]">
              {item.title && (
                <h3 className="text-white font-medium text-sm">{item.title}</h3>
              )}
              
              {item.location && (
                <p className="text-white/80 text-xs flex items-center gap-1 mt-1">
                  <MapPin size={12} />
                  {item.location}
                </p>
              )}
              
              {item.description && (
                <p className="text-white/70 text-xs mt-1 line-clamp-2">
                  {item.description}
                </p>
              )}
            </div>

            {/* Category tag */}
            {item.category && (
              <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-sm flex items-center gap-1 z-[14] border border-white/20">
                <Tag size={10} />
                {item.category}
              </div>
            )}
            
            {/* Decorative elements that appear on hover */}
            {hoveredImage === (item.id || String(index)) && (
              <div className="absolute inset-0 pointer-events-none z-[12]">
                <div className="fingerprint-scan"></div>
              </div>
            )}
          </div>
        </a.div>
      ))}
    </div>
  );
};

export default Masonry;
